import api from './axiosInstance';
import { CustomerGroup, CreateCustomerGroupData, UpdateCustomerGroupData } from '../types/customerGroup';

export interface CustomerGroupFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  status?: 'active' | 'inactive';
  customerId?: string;
}

class CustomerGroupService {
  /**
   * Get customer groups with pagination and filters
   */
  async getCustomerGroups(filters: CustomerGroupFilters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.customerId) params.append('customerId', filters.customerId);

      const response = await api.get(`/customer-groups?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer groups:', error);
      throw error;
    }
  }

  /**
   * Get all active customer groups for dropdown
   */
  async getAllCustomerGroups(): Promise<CustomerGroup[]> {
    try {
      const response = await api.get('/customer-groups/all?status=active');
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching all customer groups:', error);
      return [];
    }
  }

  /**
   * Get customer group by ID
   */
  async getCustomerGroupById(id: string): Promise<CustomerGroup | null> {
    try {
      const response = await api.get(`/customer-groups/${id}`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching customer group:', error);
      throw error;
    }
  }

  /**
   * Create new customer group
   */
  async createCustomerGroup(data: CreateCustomerGroupData): Promise<CustomerGroup> {
    try {
      const response = await api.post('/customer-groups', data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to create customer group');
    } catch (error) {
      console.error('Error creating customer group:', error);
      throw error;
    }
  }

  /**
   * Update customer group
   */
  async updateCustomerGroup(id: string, data: UpdateCustomerGroupData): Promise<CustomerGroup> {
    try {
      const response = await api.patch(`/customer-groups/${id}`, data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to update customer group');
    } catch (error) {
      console.error('Error updating customer group:', error);
      throw error;
    }
  }

  /**
   * Activate customer group
   */
  async activateCustomerGroup(id: string): Promise<CustomerGroup> {
    try {
      const response = await api.patch(`/customer-groups/${id}/activate`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to activate customer group');
    } catch (error) {
      console.error('Error activating customer group:', error);
      throw error;
    }
  }

  /**
   * Deactivate customer group
   */
  async deactivateCustomerGroup(id: string): Promise<CustomerGroup> {
    try {
      const response = await api.patch(`/customer-groups/${id}/deactivate`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to deactivate customer group');
    } catch (error) {
      console.error('Error deactivating customer group:', error);
      throw error;
    }
  }
}

const customerGroupService = new CustomerGroupService();
export default customerGroupService;
